// This project has no tsconfig.json, so this reference is needed to pull in
// react-app-env.d.ts's ambient declarations (react-scripts types, *.css
// wildcard module) — an unimported .d.ts otherwise isn't part of this file's
// inferred TS project.
/// <reference path="../../../../../react-app-env.d.ts" />
import React from 'react';
import { useRestaurant } from '../../contexts/RestaurantContext.tsx';
import { fetchRestaurantSettingsPublic } from '../../services/api.ts';
import { Calendar, Clock, Users, MapPin, User, Phone, Mail, Check, ChevronLeft, Cake, DoorOpen } from 'lucide-react';
import '../../styles/confirmation-view.css';

interface ConfirmationViewProps {
  reservationData: {
    date: Date | null;
    time: string;
    guests: number;
    name: string;
    phone: string;
    email: string;
    tableId: string;
    tableIds: string[];
    notes: string;
    birthdate: string;
    salaId: string;
  };
  restaurantSlug: string;
  managementLink?: string;
  onConfirm: () => Promise<boolean> | boolean;
  onBack: () => void;
}

const ConfirmationView: React.FC<ConfirmationViewProps> = ({
  reservationData,
  restaurantSlug,
  managementLink,
  onConfirm,
  onBack
}) => {
  const { floors } = useRestaurant();
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [requiresClientConfirmation, setRequiresClientConfirmation] = React.useState(false);

  React.useEffect(() => {
    let isMounted = true;

    const loadRestaurantSettings = async () => {
      if (!restaurantSlug) {
        if (isMounted) {
          setRequiresClientConfirmation(false);
        }
        return;
      }

      try {
        const data = await fetchRestaurantSettingsPublic(restaurantSlug);
        if (isMounted) {
          setRequiresClientConfirmation(Boolean(data?.reservo_confirmacao_cliente));
        }
      } catch (error) {
        console.error('Erro ao carregar configurações públicas na confirmação:', error);
        if (isMounted) {
          setRequiresClientConfirmation(false);
        }
      }
    };

    loadRestaurantSettings();

    return () => {
      isMounted = false;
    };
  }, [restaurantSlug]);

  const formatBirthdate = (isoDate?: string) => {
    if (!isoDate) return '';
    const [datePart] = isoDate.split('T');
    const parts = datePart?.split('-');
    if (!parts || parts.length !== 3) return isoDate;
    const [year, month, day] = parts.map(Number);
    if (!year || !month || !day) return isoDate;
    const parsed = new Date(year, month - 1, day);
    if (Number.isNaN(parsed.getTime())) return isoDate;
    return parsed.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const isReservationForToday = React.useMemo(() => {
    if (!reservationData.date) return false;

    const reservationDate = new Date(reservationData.date);
    const today = new Date();

    reservationDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    return reservationDate.getTime() === today.getTime();
  }, [reservationData.date]);

  const showWhatsappConfirmationGuidance = requiresClientConfirmation && !isReservationForToday;
  
  const allTables = floors.flatMap(floor => floor.tables ?? []);
  const selectedTableIds = Array.isArray(reservationData.tableIds) && reservationData.tableIds.length > 0
    ? reservationData.tableIds
    : (reservationData.tableId ? [reservationData.tableId] : []);
  const selectedTables = allTables.filter((table) => (
    selectedTableIds.some((tableId) => String(table.id) === String(tableId))
  ));

  const tableFloor = floors.find(floor =>
    floor.tables?.some(table =>
      selectedTableIds.some((tableId) => String(table.id) === String(tableId))
    )
  );

  const salaId = reservationData.salaId ? String(reservationData.salaId) : '';
  const selectedFloor = tableFloor
    ?? floors.find(floor => String(floor.id) === salaId);

  const handleConfirm = async () => {
    try {
      const result = await onConfirm();
      if (result !== false) {
        setShowSuccess(true);
      }
    } catch (error) {
      console.error('Erro ao confirmar reserva publica:', error);
    }
  };
  
  if (showSuccess) {
    return (
      <div className="success-message">
        <div className="success-icon">
          <Check size={48} />
        </div>
        <h2>{showWhatsappConfirmationGuidance ? 'Reserva Recebida!' : 'Reserva Confirmada!'}</h2>
        <p>
          {showWhatsappConfirmationGuidance
            ? 'Sua solicitação foi registrada com sucesso.'
            : 'Obrigado pela sua reserva. Esperamos vê-lo em breve!'}
        </p>
        <div className="confirmation-details">
          <div className="detail-row">
            <Calendar size={18} />
            <span>{reservationData.date ? reservationData.date.toLocaleDateString('pt-BR', {
              weekday: 'long',
              month: 'long',
              day: 'numeric'
            }) : 'Data não selecionada'}</span>
          </div>
          <div className="detail-row">
            <Clock size={18} />
            <span>{reservationData.time}</span>
          </div>
          <div className="detail-row">
            <Users size={18} />
            <span>{reservationData.guests} pessoas</span>
          </div>
        </div>
        {managementLink && (
          <a className="reservation-management-link" href={managementLink}>
            Alterar ou cancelar reserva
          </a>
        )}
        {showWhatsappConfirmationGuidance && (
          <div className="confirmation-guidance-card">
            <Phone size={18} />
            <div>
              <strong>Confirmação via WhatsApp</strong>
              <p>
                No dia da reserva, o Reservo enviará uma mensagem no seu WhatsApp.
                Para manter a reserva ativa, clique no botão <strong>Confirmar</strong> na mensagem recebida.
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }
  
  return (
    <div className="confirmation-view">
      <div className="confirmation-header">
        <h2>Revise sua Reserva</h2>
        <p>Verifique os detalhes da sua reserva antes de confirmar</p>
      </div>

      {requiresClientConfirmation && (
        <div className={`confirmation-guidance-banner${isReservationForToday ? ' same-day' : ''}`}>
          <Phone size={18} />
          <div>
            <strong>
              {isReservationForToday ? 'Confirmação imediata' : 'Confirmação via WhatsApp'}
            </strong>
            <p>
              {isReservationForToday
                ? 'Como a reserva é para hoje, ela será confirmada automaticamente após o envio.'
                : 'No dia da reserva, o Reservo enviará uma mensagem no seu WhatsApp. Para manter a reserva ativa, clique no botão Confirmar nessa mensagem.'}
            </p>
          </div>
        </div>
      )}
      
      <div className="confirmation-details">
        <div className="detail-section">
          <h3>Detalhes da Reserva</h3>
          
          <div className="detail-item">
            <Calendar size={18} />
            <div>
              <strong>Data</strong>
              <p>{reservationData.date ? reservationData.date.toLocaleDateString('pt-BR', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              }) : 'Data não selecionada'}</p>
            </div>
          </div>
          
          <div className="detail-item">
            <Clock size={18} />
            <div>
              <strong>Horário</strong>
              <p>{reservationData.time}</p>
            </div>
          </div>
          
          <div className="detail-item">
            <Users size={18} />
            <div>
              <strong>Número de Pessoas</strong>
              <p>{reservationData.guests} {reservationData.guests === 1 ? 'pessoa' : 'pessoas'}</p>
            </div>
          </div>

          <div className="detail-item">
            <DoorOpen size={18} />
            <div>
              <strong>Sala</strong>
              <p>{selectedFloor?.name ?? 'Sala não selecionada'}</p>
            </div>
          </div>
          
          <div className="detail-item">
            <MapPin size={18} />
            <div>
              <strong>Mesa{selectedTables.length > 1 ? 's' : ''}</strong>
              <p>
                {selectedTables.length > 0
                  ? selectedTables.map((table) => table.name).join(', ')
                  : 'Mesa não selecionada'}
                {tableFloor ? ` (${tableFloor.name})` : ''}
              </p>
            </div>
          </div>
        </div>
        
        <div className="detail-section">
          <h3>Informações do Cliente</h3>
          
          <div className="detail-item">
            <User size={18} />
            <div>
              <strong>Nome</strong>
              <p>{reservationData.name}</p>
            </div>
          </div>
          
          <div className="detail-item">
            <Phone size={18} />
            <div>
              <strong>Telefone</strong>
              <p>{reservationData.phone}</p>
            </div>
          </div>
          
          {reservationData.email && (
            <div className="detail-item">
              <Mail size={18} />
              <div>
                <strong>E-mail</strong>
                <p>{reservationData.email}</p>
              </div>
            </div>
          )}

          {reservationData.birthdate && (
            <div className="detail-item">
              <Cake size={18} />
              <div>
                <strong>Data de Nascimento</strong>
                <p>{formatBirthdate(reservationData.birthdate)}</p>
              </div>
            </div>
          )}
          
          {reservationData.notes && (
            <div className="detail-item notes">
              <strong>Observações</strong>
              <p>{reservationData.notes}</p>
            </div>
          )}
        </div>
      </div>
      
      <div className="confirmation-actions">
        <button className="btn-confirm" onClick={handleConfirm}>
          <Check size={18} />
          Confirmar Reserva
        </button>

        <button className="btn-back" onClick={onBack}>
          <ChevronLeft size={18} />
          Voltar
        </button>
      </div>
      
      <div className="confirmation-policy">
        <p>Política de Cancelamento: Reservas podem ser canceladas até 2 horas antes do horário agendado.</p>
      </div>
    </div>
  );
};

export default ConfirmationView;
